import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import Form from '../../../common/Form';
import Select from '../../../common/Form/Select';
import Input from '../../../common/Form/Input';
import TextArea from '../../../common/Form/TextArea';
import Upload from '../../../common/Form/FileUpload';
import IconToolTip from '../../../common/IconToolTip';
import { closeModalAction } from '../../../actions/modal';
import { customerApprovalRecievedRequest, revokeCustomerApprovalRequest } from '../../../actions/service_request';
import { actualValueForDropdown } from '../../../utils/common';
import { BillingApprovalReason, BillingStatus, billingApprovalReasonOptions, maxTicketsPerBillingApproval } from '../CustomerBillingController';

export const markBillingApprovalFormName = 'customer_approval_recieved';
export const revokeBillingApprovalFormName = 'revoke_customer_approval';

const maxAttachmentSizeBytes = 20 * 1000 * 1000;

// Only worth showing for a batch: for a single ticket the modal heading already names it.
const SelectedTickets = ({ tickets }) => {
    if (tickets.length <= 1) return null;
    return (
        <div className='bg-gray-50 rounded-lg px-4 py-3'>
            <p className='text-font11 text-scogogray'>
                Tickets Selected : <span className='text-font12 text-black font-medium'>{tickets.length}</span>
            </p>
            <div className='flex flex-wrap gap-2 mt-2' style={{ maxHeight: '8rem', overflow: 'auto' }}>
                {tickets.map((ticket) => (
                    <IconToolTip key={ticket.id} title={ticket.ticket_number} placement='bottom'>
                        <span className='bg-white border border-scogoddd rounded-md text-font10 px-2 py-1 whitespace-nowrap'>{ticket.ticket_number}</span>
                    </IconToolTip>
                ))}
            </div>
        </div>
    );
};

// All three billing actions use the same modal; received keeps the original approval fields.
export default function BillingApproval({ tickets = [], mode = 'received' }) {
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading);
    const isReceived = mode === 'received';

    const defaultValues = { reason: '', comment: '', reference: '', attachments: undefined, remark: '' };
    const methods = useForm({ defaultValues, mode: 'all' });
    const selectedReason = isReceived ? actualValueForDropdown(methods.watch('reason')) : null;
    const isAttachmentRequired = selectedReason === BillingApprovalReason.APPROVAL_ATTACHMENT;

    const submit = (formValues) => {
        const payload = {
            ticket_ids: tickets.map((ticket) => ticket.id),
            billing_status: isReceived ? BillingStatus.RECEIVED : mode === 'rejected' ? BillingStatus.REJECTED : BillingStatus.SUBMITTED,
            reason: isReceived ? actualValueForDropdown(formValues.reason) : mode === 'rejected' ? BillingStatus.REJECTED : BillingStatus.SUBMITTED,
        };
        if (isReceived) {
            if (formValues.comment?.trim()) payload.comment = formValues.comment.trim();
            if (formValues.reference?.trim()) payload.reference = formValues.reference.trim();
        } else {
            payload.comment = formValues.remark.trim();
        }

        dispatch(customerApprovalRecievedRequest({ payload, files: isReceived ? formValues.attachments : undefined, formName: markBillingApprovalFormName }));
    };

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            submitButtonText={isReceived ? 'Mark Customer Billing Received' : mode === 'rejected' ? 'Reject Customer Billing' : 'Submit Customer Billing'}
            onCancel={() => dispatch(closeModalAction())}
            showSubmit
            submitContainerClassName='flex gap-4 justify-end py-6 px-6 border-t border-gray-200 mt-4'
            isLoading={formButtonLoading[markBillingApprovalFormName]}
            isSubmitDisabled={tickets.length === 0 || tickets.length > maxTicketsPerBillingApproval}
            submitButtonHoverText={tickets.length > maxTicketsPerBillingApproval ? `At most ${maxTicketsPerBillingApproval} tickets can be marked at once` : ''}
        >
            <div className='px-6 py-2' style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <SelectedTickets tickets={tickets} />
                {tickets.length > maxTicketsPerBillingApproval && (
                    <p className='text-scogoclosed text-font11 mt-2'>Only {maxTicketsPerBillingApproval} tickets can be marked in one go.</p>
                )}
                {isReceived ? (
                    <>
                        <div className='grid grid-cols-1 md:grid-cols-2 mt-2'>
                            <Select name='reason' label='Approval Reason' options={billingApprovalReasonOptions} required isClearable={false} />
                            <Input name='reference' label='Customer Mail Reference' placeholder='RE: Approval for August invoices' />
                        </div>
                        <TextArea name='comment' label='Comment' placeholder='Approval received from the customer' />
                        <Upload
                            label='Approval Attachments'
                            name='attachments'
                            isMulti
                            appendFiles
                            showText
                            uploadText='Attach approval mail / document'
                            required={isAttachmentRequired}
                            maxSizeBytes={maxAttachmentSizeBytes}
                        />
                    </>
                ) : (
                    <TextArea
                        name='remark'
                        label='Remark'
                        placeholder={mode === 'rejected' ? 'Reason for rejecting customer billing' : 'Remark for customer billing submission'}
                        required
                        validate={(value) => (value?.trim() ? true : 'Required')}
                    />
                )}
            </div>
        </Form>
    );
}

// Takes the billing approval back off tickets that have not been invoiced yet.
export const RevokeBillingApproval = ({ tickets = [] }) => {
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading);

    const methods = useForm({ defaultValues: { remark: '' }, mode: 'all' });

    const submit = (formValues) => {
        const payload = {
            ticket_ids: tickets.map((ticket) => ticket.id),
            remark: formValues.remark.trim(),
        };
        dispatch(revokeCustomerApprovalRequest({ payload, formName: revokeBillingApprovalFormName }));
    };

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            submitButtonText='Revoke Approval'
            onCancel={() => dispatch(closeModalAction())}
            showSubmit
            submitContainerClassName='flex gap-4 justify-end py-6 px-6 border-t border-gray-200 mt-4'
            isLoading={formButtonLoading[revokeBillingApprovalFormName]}
            isSubmitDisabled={tickets.length === 0 || tickets.length > maxTicketsPerBillingApproval}
        >
            <div className='px-6 py-2' style={{ maxHeight: '60vh', overflow: 'auto' }}>
                <SelectedTickets tickets={tickets} />
                <TextArea
                    name='remark'
                    label='Reason For Revoking'
                    placeholder='Customer withdrew the approval'
                    required
                    validate={(value) => (value?.trim() ? true : 'Required')}
                />
            </div>
        </Form>
    );
};
